import { useState, useEffect } from "react";
import {
  collection, getDocs, addDoc, doc, updateDoc,
  query, orderBy, serverTimestamp, getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";

const G = {
  bg:"#FAF9F7",s1:"#FFFFFF",s2:"#F4F2EF",border:"#E2DFD9",border2:"#D4D0CA",
  black:"#1C1917",off:"#3D3830",muted:"#8C8078",dim:"#C8C0B8",
  orange:"#D2611A",green:"#16A34A",red:"#DC2626",amber:"#D97706",blue:"#2563EB",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",
  mono:"'IBM Plex Mono',monospace",
};

const ESTADOS = ["cotizacion","confirmado","produccion","embarque","aduana","entregado"];
const ESTADO_COLOR = { cotizacion:G.muted, confirmado:G.blue, produccion:G.amber, embarque:G.orange, aduana:G.amber, entregado:G.green };
const ESTADO_LABEL = { cotizacion:"Cotización", confirmado:"Confirmado", produccion:"En producción", embarque:"En tránsito", aduana:"En aduana", entregado:"Entregado" };

function ModalNuevoPedido({ ciclos, usuarios, productos, onGuardado, onClose }) {
  const [form, setForm] = useState({
    compradorId:"", compradorNombre:"", compradorEmpresa:"",
    cicloId:"",
    estado:"cotizacion",
    items:[{ productoId:"", productoNombre:"", cantidad:1, precio_unit:0, unidad:"u" }],
    notas:"",
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  const addItem  = ()=>setForm(p=>({...p, items:[...p.items, {productoId:"",productoNombre:"",cantidad:1,precio_unit:0,unidad:"u"}]}));
  const removeItem = (i)=>setForm(p=>({...p, items:p.items.filter((_,j)=>j!==i)}));
  const setItem  = (i,k,v)=>setForm(p=>({ ...p, items:p.items.map((it,j)=>j===i?{...it,[k]:v}:it) }));

  const selectProducto = (i, pid) => {
    const prod = productos.find(p=>p.id===pid);
    if (prod) {
      setItem(i,"productoId",pid);
      setItem(i,"productoNombre",prod.nombre);
      setItem(i,"precio_unit",prod.precio_venta||0);
      setItem(i,"unidad",prod.unidad||"u");
    } else {
      setItem(i,"productoId",pid);
    }
  };

  const selectComprador = (uid) => {
    const u = usuarios.find(u=>u.id===uid);
    setForm(p=>({...p, compradorId:uid, compradorNombre:u?.nombre||"", compradorEmpresa:u?.empresa||""}));
  };

  const totalUSD = form.items.reduce((s,it)=>s+(parseFloat(it.cantidad)||0)*(parseFloat(it.precio_unit)||0),0);

  const guardar = async () => {
    if (!form.compradorId) { setError("Seleccioná un comprador."); return; }
    if (!form.cicloId)     { setError("Seleccioná un ciclo."); return; }
    if (!form.items[0].productoId) { setError("Agregá al menos un producto."); return; }
    setSaving(true);
    try {
      const pedidoRef = await addDoc(collection(db,"pedidos"), {
        ...form,
        total_usd:    totalUSD,
        total_con_iva: totalUSD * 1.21,
        creadoEn:     serverTimestamp(),
        updatedAt:    serverTimestamp(),
        timeline:[{ estado:"cotizacion", fecha:new Date().toISOString(), nota:"Pedido creado desde admin" }],
      });
      // Actualizar CBM del ciclo
      if (form.cicloId) {
        const ciclo = ciclos.find(c=>c.id===form.cicloId);
        if (ciclo) {
          const cbmNuevo = form.items.reduce((s,it)=>{
            const prod = productos.find(p=>p.id===it.productoId);
            return s + (prod?.cbm||0)*(parseFloat(it.cantidad)||0);
          },0);
          await updateDoc(doc(db,"ciclos",form.cicloId), {
            cbm_usado: (ciclo.cbm_usado||0) + cbmNuevo,
            updatedAt: serverTimestamp(),
          });
        }
      }
      onGuardado();
    } catch(e) { setError("Error: "+e.message); }
    setSaving(false);
  };

  const inp = { width:"100%", background:"transparent", border:"none", borderBottom:`1.5px solid ${G.border2}`, color:G.black, fontFamily:G.sans, fontSize:13, padding:"7px 0", outline:"none", fontWeight:400 };
  const lbl = { fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:5, fontWeight:600, display:"block" };
  const sel = { ...inp, background:G.s2, border:`1px solid ${G.border}`, padding:"7px 10px" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(28,25,23,.55)", zIndex:300, display:"flex", alignItems:"flex-start", justifyContent:"center", overflow:"auto", padding:"24px 20px" }}>
      <div style={{ background:G.s1, border:`1.5px solid ${G.border}`, padding:"28px 32px", width:"100%", maxWidth:640 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:22 }}>
          <div style={{ fontFamily:G.sans, fontSize:18, color:G.black, fontWeight:700 }}>Nuevo pedido</div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, color:G.muted, cursor:"pointer" }}>×</button>
        </div>

        {/* Comprador + Ciclo */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
          <div>
            <label style={lbl}>Comprador *</label>
            <select value={form.compradorId} onChange={e=>selectComprador(e.target.value)} style={sel}>
              <option value="">Seleccioná un cliente...</option>
              {usuarios.filter(u=>u.rol==="comprador"||u.rol==="vendedor").map(u=>(
                <option key={u.id} value={u.id}>{u.nombre} {u.empresa?`· ${u.empresa}`:""}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Ciclo de contenedor *</label>
            <select value={form.cicloId} onChange={e=>setForm(p=>({...p,cicloId:e.target.value}))} style={sel}>
              <option value="">Seleccioná un ciclo...</option>
              {ciclos.filter(c=>c.estado!=="cerrado").map(c=>(
                <option key={c.id} value={c.id}>{c.nombre||c.id} · {c.cbm_usado||0}/{c.cbm_max||68} CBM</option>
              ))}
            </select>
          </div>
        </div>

        {/* Estado */}
        <div style={{ marginBottom:16 }}>
          <label style={lbl}>Estado inicial</label>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {ESTADOS.map(e=>(
              <button key={e} onClick={()=>setForm(p=>({...p,estado:e}))}
                style={{ padding:"4px 12px", fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", background:form.estado===e?ESTADO_COLOR[e]:"transparent", color:form.estado===e?"#fff":G.muted, border:`1.5px solid ${form.estado===e?ESTADO_COLOR[e]:G.border}`, cursor:"pointer", fontWeight:form.estado===e?700:500 }}>
                {ESTADO_LABEL[e]}
              </button>
            ))}
          </div>
        </div>

        {/* Items */}
        <div style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <label style={lbl}>Productos del pedido *</label>
            <button onClick={addItem} style={{ fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", background:G.s2, color:G.orange, border:`1px solid ${G.border}`, padding:"4px 10px", cursor:"pointer", fontWeight:600 }}>+ Línea</button>
          </div>
          {form.items.map((it,i)=>(
            <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 80px 100px 28px", gap:8, marginBottom:8, alignItems:"end" }}>
              <div>
                {i===0&&<label style={lbl}>Producto</label>}
                <select value={it.productoId} onChange={e=>selectProducto(i,e.target.value)} style={sel}>
                  <option value="">Seleccioná...</option>
                  {productos.map(p=><option key={p.id} value={p.id}>{p.nombre} · {p.rubro}</option>)}
                </select>
              </div>
              <div>
                {i===0&&<label style={lbl}>Cant.</label>}
                <input type="number" min="1" value={it.cantidad} onChange={e=>setItem(i,"cantidad",e.target.value)} style={inp}/>
              </div>
              <div>
                {i===0&&<label style={lbl}>Precio s/IVA</label>}
                <input type="number" value={it.precio_unit} onChange={e=>setItem(i,"precio_unit",e.target.value)} style={inp}/>
              </div>
              <button onClick={()=>removeItem(i)} disabled={form.items.length===1} style={{ fontFamily:G.mono, fontSize:14, color:G.red, background:"none", border:"none", cursor:"pointer", paddingBottom:8, opacity:form.items.length===1?.3:1 }}>×</button>
            </div>
          ))}
          {/* Total */}
          <div style={{ display:"flex", justifyContent:"flex-end", marginTop:8, padding:"10px 12px", background:G.s2, border:`1px solid ${G.border}` }}>
            <div>
              <span style={{ fontFamily:G.sans, fontSize:10, color:G.muted, marginRight:12 }}>Total sin IVA:</span>
              <span style={{ fontFamily:G.mono, fontSize:14, color:G.black, fontWeight:700 }}>USD {totalUSD.toLocaleString("es-AR",{minimumFractionDigits:2})}</span>
              <span style={{ fontFamily:G.mono, fontSize:10, color:G.muted, marginLeft:12 }}>c/IVA: USD {(totalUSD*1.21).toLocaleString("es-AR",{minimumFractionDigits:2})}</span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={lbl}>Notas internas</label>
          <input value={form.notas} onChange={e=>setForm(p=>({...p,notas:e.target.value}))} placeholder="Obra Palermo, entrega coordinada..." style={inp}
            onFocus={e=>e.target.style.borderBottomColor=G.orange} onBlur={e=>e.target.style.borderBottomColor=G.border2}/>
        </div>

        {error && <div style={{ fontFamily:G.sans, fontSize:11, color:G.red, marginBottom:16, padding:"8px 10px", background:"#FEF2F2" }}>{error}</div>}

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={guardar} disabled={saving} style={{ flex:1, padding:"11px 0", background:G.orange, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700 }}>
            {saving?"Guardando...":"Crear pedido →"}
          </button>
          <button onClick={onClose} style={{ padding:"11px 16px", background:"transparent", color:G.muted, fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:`1.5px solid ${G.border}`, cursor:"pointer" }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminPedidos() {
  const [pedidos,   setPedidos]   = useState([]);
  const [ciclos,    setCiclos]    = useState([]);
  const [usuarios,  setUsuarios]  = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [filtro,    setFiltro]    = useState("todos");
  const [detalle,   setDetalle]   = useState(null);

  const cargar = async () => {
    setLoading(true);
    const [pSnap, cSnap, uSnap, prSnap] = await Promise.all([
      getDocs(query(collection(db,"pedidos"), orderBy("creadoEn","desc"))).catch(()=>({docs:[]})),
      getDocs(collection(db,"ciclos")).catch(()=>({docs:[]})),
      getDocs(collection(db,"usuarios")).catch(()=>({docs:[]})),
      getDocs(collection(db,"productos")).catch(()=>({docs:[]})),
    ]);
    setPedidos( pSnap.docs.map(d=>({id:d.id,...d.data()})));
    setCiclos(  cSnap.docs.map(d=>({id:d.id,...d.data()})));
    setUsuarios(uSnap.docs.map(d=>({id:d.id,...d.data()})));
    setProductos(prSnap.docs.map(d=>({id:d.id,...d.data()})));
    setLoading(false);
  };
  useEffect(()=>{ cargar(); },[]);

  const cambiarEstado = async (id, estado) => {
    await updateDoc(doc(db,"pedidos",id), {
      estado, updatedAt:serverTimestamp(),
      timeline: [...(pedidos.find(p=>p.id===id)?.timeline||[]), { estado, fecha:new Date().toISOString() }],
    });
    cargar();
  };

  const filtrados = filtro==="todos" ? pedidos : pedidos.filter(p=>p.estado===filtro);

  const counts = ESTADOS.reduce((a,e)=>({...a,[e]:pedidos.filter(p=>p.estado===e).length}),{});

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:G.sans, fontSize:22, color:G.black, fontWeight:700, marginBottom:3 }}>Pedidos</div>
          <div style={{ fontFamily:G.mono, fontSize:10, color:G.muted }}>{pedidos.length} pedidos totales</div>
        </div>
        <button onClick={()=>setModal(true)} style={{ padding:"9px 22px", background:G.orange, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700 }}>
          + Nuevo pedido
        </button>
      </div>

      {/* Filtros por estado */}
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:20 }}>
        <button onClick={()=>setFiltro("todos")} style={{ padding:"5px 14px", fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", background:filtro==="todos"?G.black:"transparent", color:filtro==="todos"?"#fff":G.muted, border:`1.5px solid ${filtro==="todos"?G.black:G.border}`, cursor:"pointer", fontWeight:600 }}>
          Todos ({pedidos.length})
        </button>
        {ESTADOS.map(e=>(
          <button key={e} onClick={()=>setFiltro(e)} style={{ padding:"5px 14px", fontFamily:G.sans, fontSize:9, letterSpacing:1.5, textTransform:"uppercase", background:filtro===e?ESTADO_COLOR[e]:"transparent", color:filtro===e?"#fff":G.muted, border:`1.5px solid ${filtro===e?ESTADO_COLOR[e]:G.border}`, cursor:"pointer", fontWeight:filtro===e?700:500 }}>
            {ESTADO_LABEL[e]} ({counts[e]||0})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:"center", fontFamily:G.sans, fontSize:12, color:G.muted }}>Cargando...</div>
      ) : filtrados.length===0 ? (
        <div style={{ padding:48, textAlign:"center", background:G.s1, border:`1px solid ${G.border}` }}>
          <div style={{ fontFamily:G.sans, fontSize:14, color:G.muted, marginBottom:12 }}>Sin pedidos en esta categoría.</div>
          <button onClick={()=>setModal(true)} style={{ padding:"9px 22px", background:G.orange, color:"#fff", fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", border:"none", cursor:"pointer", fontWeight:700 }}>
            + Crear primer pedido
          </button>
        </div>
      ) : (
        <div style={{ background:G.border }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px 100px 100px 120px", background:G.s2, padding:"8px 14px", gap:8 }}>
            {["Cliente","Ciclo","Items","Total s/IVA","Estado","Acciones"].map(h=>(
              <div key={h} style={{ fontFamily:G.sans, fontSize:8, letterSpacing:2, color:G.muted, textTransform:"uppercase", fontWeight:600 }}>{h}</div>
            ))}
          </div>
          {filtrados.map(p=>{
            const ciclo = ciclos.find(c=>c.id===p.cicloId);
            return (
              <div key={p.id} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 80px 100px 100px 120px", background:G.s1, padding:"11px 14px", gap:8, borderBottom:`1px solid ${G.border}`, alignItems:"center" }}>
                <div>
                  <div style={{ fontFamily:G.sans, fontSize:12, color:G.black, fontWeight:600 }}>{p.compradorNombre||"—"}</div>
                  <div style={{ fontFamily:G.sans, fontSize:10, color:G.muted }}>{p.compradorEmpresa||""}</div>
                </div>
                <div style={{ fontFamily:G.mono, fontSize:10, color:G.muted }}>{ciclo?.nombre||p.cicloId||"—"}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.off }}>{(p.items||[]).length} items</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.off }}>USD {(p.total_usd||0).toLocaleString("es-AR",{minimumFractionDigits:2})}</div>
                <div>
                  <span style={{ fontFamily:G.mono, fontSize:8, letterSpacing:1.5, textTransform:"uppercase", color:ESTADO_COLOR[p.estado]||G.muted, border:`1px solid ${ESTADO_COLOR[p.estado]||G.muted}`, padding:"2px 6px" }}>
                    {ESTADO_LABEL[p.estado]||p.estado}
                  </span>
                </div>
                <div style={{ display:"flex", gap:4 }}>
                  <button onClick={()=>setDetalle(p)} style={{ fontFamily:G.mono, fontSize:12, color:G.blue, background:"none", border:"none", cursor:"pointer", padding:"0 3px" }}>🔍</button>
                  <select value={p.estado} onChange={e=>cambiarEstado(p.id,e.target.value)}
                    style={{ fontFamily:G.sans, fontSize:8, letterSpacing:1, color:G.off, background:G.s2, border:`1px solid ${G.border}`, padding:"3px 6px", cursor:"pointer" }}>
                    {ESTADOS.map(e=><option key={e} value={e}>{ESTADO_LABEL[e]}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detalle pedido */}
      {detalle && (
        <div style={{ position:"fixed", inset:0, background:"rgba(28,25,23,.5)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:G.s1, border:`1.5px solid ${G.border}`, padding:"28px 32px", width:500, maxWidth:"95vw", maxHeight:"85vh", overflow:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div style={{ fontFamily:G.sans, fontSize:16, color:G.black, fontWeight:700 }}>Detalle del pedido</div>
              <button onClick={()=>setDetalle(null)} style={{ background:"none", border:"none", fontSize:20, color:G.muted, cursor:"pointer" }}>×</button>
            </div>
            <div style={{ fontFamily:G.sans, fontSize:12, color:G.muted, marginBottom:16 }}>
              <strong style={{color:G.black}}>{detalle.compradorNombre}</strong> · {detalle.compradorEmpresa}
            </div>
            {(detalle.items||[]).map((it,i)=>(
              <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 60px 80px 80px", gap:8, padding:"8px 0", borderBottom:`1px solid ${G.border}` }}>
                <div style={{ fontFamily:G.sans, fontSize:12, color:G.off }}>{it.productoNombre||it.productoId}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.muted }}>{it.cantidad} {it.unidad}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.off }}>USD {it.precio_unit}</div>
                <div style={{ fontFamily:G.mono, fontSize:11, color:G.black, fontWeight:600 }}>USD {((it.cantidad||0)*(it.precio_unit||0)).toFixed(2)}</div>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"flex-end", marginTop:12, gap:20 }}>
              <span style={{ fontFamily:G.mono, fontSize:12, color:G.muted }}>Total s/IVA: USD {(detalle.total_usd||0).toFixed(2)}</span>
              <span style={{ fontFamily:G.mono, fontSize:14, color:G.orange, fontWeight:700 }}>c/IVA: USD {(detalle.total_con_iva||0).toFixed(2)}</span>
            </div>
            {detalle.notas && <div style={{ marginTop:12, fontFamily:G.sans, fontSize:11, color:G.muted }}>{detalle.notas}</div>}
            {/* Timeline */}
            {(detalle.timeline||[]).length>0 && (
              <div style={{ marginTop:16 }}>
                <div style={{ fontFamily:G.sans, fontSize:9, letterSpacing:2, textTransform:"uppercase", color:G.muted, marginBottom:8, fontWeight:600 }}>Historial</div>
                {detalle.timeline.map((t,i)=>(
                  <div key={i} style={{ display:"flex", gap:10, padding:"5px 0", borderBottom:`1px solid ${G.border}` }}>
                    <span style={{ fontFamily:G.mono, fontSize:8, color:ESTADO_COLOR[t.estado]||G.muted, border:`1px solid ${ESTADO_COLOR[t.estado]||G.muted}`, padding:"1px 5px", flexShrink:0 }}>{ESTADO_LABEL[t.estado]||t.estado}</span>
                    <span style={{ fontFamily:G.mono, fontSize:9, color:G.muted }}>{t.fecha?.slice(0,16).replace("T"," ")||""}</span>
                    {t.nota && <span style={{ fontFamily:G.sans, fontSize:10, color:G.muted }}>{t.nota}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {modal && (
        <ModalNuevoPedido
          ciclos={ciclos} usuarios={usuarios} productos={productos}
          onGuardado={()=>{ setModal(false); cargar(); }}
          onClose={()=>setModal(false)}
        />
      )}
    </div>
  );
}
